<template>
  <nav
    class="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-t border-ink-200 pb-safe"
  >
    <div class="flex items-center justify-around px-1 py-1.5">
      <button
        v-for="item in primaryItems"
        :key="item.route"
        type="button"
        :class="[
          'flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl flex-1 transition-colors',
          isActive(item.route) ? 'text-brand-600' : 'text-ink-400'
        ]"
        @click="navigate(item.route)"
      >
        <span class="[&_svg]:w-5 [&_svg]:h-5" v-html="item.icon" />
        <span class="text-2xs font-semibold">{{ item.label }}</span>
      </button>
      <button
        type="button"
        :class="[
          'flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl flex-1 transition-colors',
          moreOpen ? 'text-brand-600' : 'text-ink-400'
        ]"
        @click="moreOpen = !moreOpen"
      >
        <span class="[&_svg]:w-5 [&_svg]:h-5">
          <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </span>
        <span class="text-2xs font-semibold">Plus</span>
      </button>
    </div>

    <!-- Drawer "Plus" -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="moreOpen"
          class="fixed inset-0 z-[100] bg-ink-900/40 backdrop-blur-sm md:hidden"
          @click="moreOpen = false"
        >
          <Transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="translate-y-full"
            enter-to-class="translate-y-0"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="translate-y-0"
            leave-to-class="translate-y-full"
          >
            <div
              v-if="moreOpen"
              class="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl pb-safe"
              @click.stop
            >
              <div class="w-12 h-1.5 rounded-full bg-ink-200 mx-auto mt-3 mb-2" />
              <div class="px-4 py-2 flex items-center justify-between">
                <h3 class="text-base font-bold tracking-tight text-ink-900">Plus d'options</h3>
                <button
                  type="button"
                  class="p-1.5 rounded-lg text-ink-400 hover:bg-ink-100"
                  @click="moreOpen = false"
                >
                  <svg
                    class="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div class="grid grid-cols-4 gap-2 p-4 pt-2">
                <button
                  v-for="item in moreItems"
                  :key="item.route"
                  type="button"
                  class="flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-ink-50 hover:bg-ink-100 transition-colors"
                  @click="navigate(item.route)"
                >
                  <span
                    :class="[
                      'w-10 h-10 rounded-xl flex items-center justify-center [&_svg]:w-5 [&_svg]:h-5',
                      isActive(item.route)
                        ? 'bg-brand-100 text-brand-600'
                        : 'bg-white text-ink-600 shadow-soft'
                    ]"
                    v-html="item.icon"
                  />
                  <span class="text-xs font-medium text-ink-700 text-center leading-tight">{{
                    item.label
                  }}</span>
                </button>
              </div>
              <div class="px-4 pb-4 pt-1">
                <button
                  type="button"
                  class="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-red-50 text-red-600 font-semibold text-sm"
                  @click="handleLogout"
                >
                  <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75"
                    />
                  </svg>
                  Déconnexion
                </button>
              </div>
            </div>
          </Transition>
        </div>
      </Transition>
    </Teleport>
  </nav>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { usePermissions } from '@/composables/usePermissions'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const { hasAnyPermission, hasAllPermissions, isSuperAdmin } = usePermissions()

const moreOpen = ref(false)

const icons = {
  home: '<svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M3 12l9-9 9 9M5 10v10h14V10"/></svg>',
  calendar:
    '<svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>',
  heart:
    '<svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"/></svg>',
  stock:
    '<svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M20 7l-8-4-8 4m16 0v10l-8 4-8-4V7m16 0l-8 4M4 7l8 4m0 0v10"/></svg>',
  desmos:
    '<svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2M9 12h6m-6 4h6"/></svg>',
  lab: '<svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M14.25 3.104v5.714a2.25 2.25 0 00.659 1.591L19 14.5M9.75 3.104a48.967 48.967 0 015 0M5 14.5L3.75 19.5h16.5L19 14.5M5 14.5h14"/></svg>',
  leave:
    '<svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3M3 11h18M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>',
  payslip:
    '<svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5l5 5v11a2 2 0 01-2 2z"/></svg>',
  establishment:
    '<svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-5h6v5M9 11h.01M15 11h.01"/></svg>',
  users:
    '<svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.4-1.8M17 20H7m10 0v-2c0-.7-.1-1.3-.4-1.8M7 20H2v-2a3 3 0 015.4-1.8M7 20v-2c0-.7.1-1.3.4-1.8m0 0a5 5 0 019.2 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>',
  profile:
    '<svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="7" r="4"/><path stroke-linecap="round" stroke-linejoin="round" d="M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2"/></svg>',
  pointage:
    '<svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z"/></svg>'
}

// 4 onglets principaux en bas
const primaryItems = computed(() => {
  const list = [
    { label: 'Accueil', route: '/', icon: icons.home, perms: [], any: false },
    {
      label: 'Agenda',
      route: '/calendar',
      icon: icons.calendar,
      perms: ['read_own_schedule', 'read_schedules'],
      any: true
    },
    {
      label: 'PEC',
      route: '/patient-care',
      icon: icons.heart,
      perms: ['read_patients', 'read_patient_cares', 'read_own_patient_cares'],
      any: true
    },
    {
      label: 'Stock',
      route: '/stock',
      icon: icons.stock,
      perms: ['write_products', 'delete_products'],
      any: true
    }
  ]
  return list.filter((i) => {
    // Sur le rôle, pas sur une permission : hasAnyPermission et
    // hasAllPermissions renvoient true pour tout rôle admin.
    if (i.superadminOnly) return isSuperAdmin.value
    return !i.perms.length || (i.any ? hasAnyPermission(i.perms) : hasAllPermissions(i.perms))
  })
})

const moreItems = computed(() => {
  const list = [
    {
      label: 'Desmos',
      route: '/desmos',
      icon: icons.desmos,
      perms: ['write_patient_cares'],
      any: false
    },
    {
      label: 'Laboratoire',
      route: '/lab-order',
      icon: icons.lab,
      perms: ['view_lab_page', 'create_lab_form'],
      any: true
    },
    { label: 'Pointage', route: '/pointage', icon: icons.pointage, perms: [], any: false },
    {
      label: 'Congés',
      route: '/leave-request',
      icon: icons.leave,
      perms: ['read_own_leave_requests', 'read_leave_requests'],
      any: true
    },
    {
      label: 'Paie',
      route: '/payslips',
      icon: icons.payslip,
      perms: ['read_payslips', 'read_own_payslips'],
      any: true
    },
    {
      label: 'Utilisateurs',
      route: '/user-management',
      icon: icons.users,
      perms: ['admin_access', 'read_users'],
      any: false
    },
    {
      label: 'Centres',
      route: '/establishments',
      icon: icons.establishment,
      perms: [],
      superadminOnly: true
    },
    {
      label: 'Profil',
      route: '/profile',
      icon: icons.profile,
      perms: ['read_own_user'],
      any: false
    }
  ]
  return list.filter((i) => {
    // Sur le rôle, pas sur une permission : hasAnyPermission et
    // hasAllPermissions renvoient true pour tout rôle admin.
    if (i.superadminOnly) return isSuperAdmin.value
    return !i.perms.length || (i.any ? hasAnyPermission(i.perms) : hasAllPermissions(i.perms))
  })
})

const isActive = (path) => {
  if (path === '/') return route.path === '/'
  return route.path.startsWith(path)
}

const navigate = (path) => {
  moreOpen.value = false
  if (route.path !== path) router.push(path)
}

const handleLogout = async () => {
  await authStore.logout()
  moreOpen.value = false
  router.push('/login')
}
</script>

<style scoped>
.pb-safe {
  padding-bottom: env(safe-area-inset-bottom, 0.5rem);
}
</style>
